import {
  inputsForTypeSwitch,
  setParameterValue,
} from '@/features/backtest/components/nodes/universal-node-helpers'
import { readParameter } from '@/features/backtest/schemas/input-activity'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import type { NodeInput } from '@/features/backtest/types/nodes'
import { isProxyEdge, type LooseEdge, type LooseNode } from './graph-lowering'

/**
 * Whether an input that can be wired or typed (a Condition's Input B) is a
 * typed number has one store: its setting (`inputBType`, the Compare Type
 * toggle), which the canvas and the validator read (connection audit step 7,
 * 2026-10-06). The input entry's `inputType`, which the engine and the recipe
 * read, follows it: this brings the entry in line at load and at save, with
 * the same rewrite the toggle uses, so the typed number and the parked wire
 * survive the switch.
 *
 * A setting that was never stored is left alone: it is not a choice the user
 * made, and the entry is what the engine has been running. An input that
 * follows its wire (the Condition's B) is kept in line by `followWire`, which
 * reads the wires as well.
 */
export function reconcileStaticMode(
  nodeType: string,
  parameters: unknown,
  inputs: NodeInput[] | undefined,
): NodeInput[] | undefined {
  const schema = UniversalNodeRegistry.getSchema(nodeType)
  if (!schema || !inputs) return inputs
  let next = inputs
  for (const inputDef of schema.inputs) {
    const union = inputDef.discriminatedUnion
    if (!union) continue
    const setting = readParameter(parameters, union.parameterName)
    if (setting !== 'node' && setting !== 'static') continue
    const entry = next.find((input) => input?.name === inputDef.id)
    if (!entry) continue
    const isStatic = entry.inputType === 'static'
    if (isStatic === (setting === 'static')) continue
    next = inputsForTypeSwitch(next, inputDef, setting)
  }
  return next
}

/** Whether an input entry holds a typed value: static, and something typed. */
export function hasTypedValue(entry: NodeInput | undefined): boolean {
  return (
    entry?.inputType === 'static' &&
    entry.value !== null &&
    entry.value !== undefined &&
    entry.value !== ''
  )
}

/**
 * An input that follows its wire (`discriminatedUnion.followsWire`, the
 * Condition's B: Chris, 2026-10-28) has no Node / Static to pick. Wired it
 * reads the wire; unwired it is the number typed in its box. Its setting
 * (`inputBType`), which saved strategies, the pre-run check, the Form and the
 * wire labels read, and its entry are brought in line with that:
 * - a wire in: Node, a typed number kept for later (`inputsForTypeSwitch`);
 * - no wire, a number typed: Static;
 * - no wire, nothing typed: Node with no source, which every check reads as
 *   missing. Never Static, which would compare to the default 0.
 * With `restoreTyped`, for a wire the user just took off (Chris, 2026-10-28:
 * "I do prefer it"), the number kept while wired comes back and is Static.
 * Only then: a run or an open never brings a kept number back on its own.
 * Proxy wires (a collapsed subnet's drawing) are not read; their canonical
 * wires are. A setting never stored that is already right stays unstored.
 * The same node comes back when nothing changes.
 */
export function followWire<N extends LooseNode>(
  node: N,
  edges: readonly LooseEdge[],
  { restoreTyped = false }: { restoreTyped?: boolean } = {},
): N {
  const schema = UniversalNodeRegistry.getSchema(node.type ?? '')
  if (!schema?.inputs.some((input) => input.discriminatedUnion?.followsWire)) return node
  const data = (node.data ?? {}) as Record<string, unknown>
  const before = Array.isArray(data.inputs) ? (data.inputs as NodeInput[]) : undefined
  let inputs = before
  let parameters = data.parameters
  for (const inputDef of schema.inputs) {
    const union = inputDef.discriminatedUnion
    if (!union?.followsWire) continue
    const wired = edges.some(
      (edge) => !isProxyEdge(edge) && edge.target === node.id && edge.targetHandle === inputDef.id,
    )
    const kept = inputs?.find((input) => input?.name === inputDef.id)?._staticValue
    const entryNow = inputs?.find((input) => input?.name === inputDef.id)
    if (
      restoreTyped &&
      !wired &&
      entryNow?.inputType !== 'static' &&
      kept !== null &&
      kept !== undefined &&
      kept !== ''
    )
      inputs = inputsForTypeSwitch(inputs, inputDef, 'static')
    const entry = inputs?.find((input) => input?.name === inputDef.id)
    const typed = hasTypedValue(entry)
    const mode = typed && !wired ? 'static' : 'node'
    if (entry?.inputType === 'static' && mode === 'node') {
      inputs = typed
        ? inputsForTypeSwitch(inputs, inputDef, 'node')
        : (inputs ?? []).map((input) =>
            input?.name === inputDef.id
              ? { name: inputDef.id, inputType: 'node' as const, source: null }
              : input,
          )
    }
    const stored = readParameter(parameters, union.parameterName) ?? 'node'
    if (stored !== mode) parameters = setParameterValue(parameters, union.parameterName, mode)
  }
  if (inputs === before && parameters === data.parameters) return node
  return {
    ...node,
    data: {
      ...data,
      ...(inputs !== before ? { inputs } : {}),
      ...(parameters !== data.parameters ? { parameters } : {}),
    },
  }
}
